import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { LanguageProvider } from './context/LanguageContext';
import { FeedbackProvider } from './context/FeedbackContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { OpenOrdersCountProvider } from './context/OpenOrdersCountContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import DashboardMetricOpenBalance from './pages/DashboardMetricOpenBalance';
import DashboardMetricPayments from './pages/DashboardMetricPayments';
import DashboardMetricOrders from './pages/DashboardMetricOrders';
import DashboardMetricCanceledNoReturn from './pages/DashboardMetricCanceledNoReturn';
import InventoryLayout from './pages/InventoryLayout';
import Orders from './pages/Orders';
import SpecialRequests from './pages/SpecialRequests';
import OrderDetail from './pages/OrderDetail';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Users from './pages/Users';
import ClientPortal from './pages/ClientPortal';
import Cart from './pages/Cart';
import MyAccount from './pages/MyAccount';
import Cashiering from './pages/Cashiering';
import CashieringCheckDetail from './pages/CashieringCheckDetail';
import AuditLogs from './pages/AuditLogs';
import Settings from './pages/Settings';
import LanguageSync from './components/LanguageSync';
import EnvironmentBanner from './components/EnvironmentBanner';

function DefaultRedirect() {
  const { isInternal } = useAuth();
  return <Navigate to={isInternal ? '/dashboard' : '/inventory'} replace />;
}

function App() {
  return (
    <LanguageProvider>
      <CssBaseline />
      <EnvironmentBanner />
      <Router>
        <FeedbackProvider>
          <AuthProvider>
          <LanguageSync />
          <OpenOrdersCountProvider>
            <CartProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/dashboard/metrics/open-balance"
                  element={
                    <PrivateRoute>
                      <DashboardMetricOpenBalance />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/dashboard/metrics/payments"
                  element={
                    <PrivateRoute>
                      <DashboardMetricPayments />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/dashboard/metrics/orders"
                  element={
                    <PrivateRoute>
                      <DashboardMetricOrders />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/dashboard/metrics/canceled-no-return"
                  element={
                    <PrivateRoute>
                      <DashboardMetricCanceledNoReturn />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inventory/*"
                  element={
                    <PrivateRoute>
                      <InventoryLayout />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/cart"
                  element={
                    <PrivateRoute>
                      <Cart />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <PrivateRoute>
                      <Orders />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/special-requests"
                  element={
                    <PrivateRoute>
                      <SpecialRequests />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/orders/:id"
                  element={
                    <PrivateRoute>
                      <OrderDetail />
                    </PrivateRoute>
                  }
                />
                <Route path="/reservations" element={<Navigate to="/orders" replace />} />
                <Route path="/sales" element={<Navigate to="/orders" replace />} />
                <Route
                  path="/cashiering"
                  element={
                    <PrivateRoute>
                      <Cashiering />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/cashiering/:checkId"
                  element={
                    <PrivateRoute>
                      <CashieringCheckDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/clients"
                  element={
                    <PrivateRoute>
                      <Clients />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/clients/:id/:tabName/:itemId"
                  element={
                    <PrivateRoute>
                      <ClientDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/clients/:id/:tabName"
                  element={
                    <PrivateRoute>
                      <ClientDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/clients/:id"
                  element={
                    <PrivateRoute>
                      <ClientDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <PrivateRoute>
                      <Users />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/portal"
                  element={
                    <PrivateRoute>
                      <ClientPortal />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/my-account"
                  element={
                    <PrivateRoute>
                      <MyAccount />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/audit-logs"
                  element={
                    <PrivateRoute>
                      <AuditLogs />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <Settings />
                    </PrivateRoute>
                  }
                />
                <Route path="/inventory-requirements" element={<Navigate to="/inventory/requirements/certified" replace />} />
                <Route path="/" element={<PrivateRoute><DefaultRedirect /></PrivateRoute>} />
              </Routes>
            </CartProvider>
          </OpenOrdersCountProvider>
          </AuthProvider>
        </FeedbackProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App;
